/* Batch #14.147 — Widget « Visites Portail Démo Public »
 *
 * Affiche un résumé temps-réel des visites du compte demo-portail (login publique).
 * Polling toutes les 30s pour rester frais.
 */
import { useEffect, useState, useCallback } from "react";
import { Eye, Users, TrendingUp, Smartphone, Monitor, Clock } from "lucide-react";
import { api } from "@/api";

const POLL_MS = 30000;

export default function DemoVisitsWidget() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const r = await api.get("/admin/demo-stats");
      setStats(r.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const t = setInterval(fetchStats, POLL_MS);
    return () => clearInterval(t);
  }, [fetchStats]);

  if (loading || !stats) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse" data-testid="demo-visits-widget-loading">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
        <div className="grid grid-cols-4 gap-2">
          {[1,2,3,4].map((i) => <div key={i} className="h-16 bg-gray-100 rounded"></div>)}
        </div>
      </div>
    );
  }

  const lastVisitRelative = stats.last_visit_at ? formatRelative(stats.last_visit_at) : "—";
  const mobilePct = stats.device_breakdown?.mobile && stats.device_breakdown?.desktop
    ? Math.round((stats.device_breakdown.mobile / (stats.device_breakdown.mobile + stats.device_breakdown.desktop)) * 100)
    : null;

  // Mini graphique : 30 derniers jours (sparkline)
  const days = stats.by_day_30d || [];
  const maxDay = Math.max(1, ...days.map((d) => d.count));
  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="demo-visits-widget">
      <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-purple-50 flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center">
          <Eye className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-indigo-900">Visites Portail Démo Public</h3>
          <p className="text-[10px] text-indigo-700">
            Dernière visite : <span className="font-medium">{lastVisitRelative}</span>
            {" · "}
            <span className="text-emerald-700">{stats.unique_ips_7d} visiteurs uniques (7j)</span>
          </p>
        </div>
        <button
          onClick={fetchStats}
          className="text-[10px] text-indigo-600 hover:text-indigo-800 px-2 py-1 rounded hover:bg-indigo-100"
          data-testid="demo-visits-refresh"
        >
          ↻ rafraîchir
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3">
        <KpiCard label="Dernière heure" value={stats.last_hour} icon={Clock} tone="rose" pulse={stats.last_hour > 0} testid="demo-visits-hour" />
        <KpiCard label="Dernières 24h" value={stats.last_24h} icon={TrendingUp} tone="amber" testid="demo-visits-24h" />
        <KpiCard label="7 derniers jours" value={stats.last_7d} icon={Users} tone="blue" testid="demo-visits-7d" />
        <KpiCard label="30 derniers jours" value={stats.last_30d} icon={Eye} tone="indigo" testid="demo-visits-30d" />
      </div>

      {/* Sparkline + Device breakdown */}
      <div className="px-3 pb-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Sparkline */}
        <div className="sm:col-span-2 bg-gray-50 rounded-lg p-2.5">
          <div className="text-[10px] font-semibold uppercase text-gray-500 mb-1.5">Distribution 30 jours</div>
          {days.length === 0 ? (
            <div className="text-xs text-gray-400 italic py-3">Aucune visite enregistrée</div>
          ) : (
            <div className="flex items-end gap-[2px] h-12">
              {fillMissingDays(days, 30).map((d) => (
                <div
                  key={d.date}
                  className={`flex-1 rounded-t transition-all ${
                    d.date === todayIso ? "bg-indigo-600" : d.count > 0 ? "bg-indigo-400" : "bg-gray-200"
                  }`}
                  style={{ height: `${Math.max(2, (d.count / maxDay) * 100)}%` }}
                  title={`${d.date} · ${d.count} visite${d.count > 1 ? "s" : ""}`}
                />
              ))}
            </div>
          )}
        </div>
        {/* Device */}
        <div className="bg-gray-50 rounded-lg p-2.5">
          <div className="text-[10px] font-semibold uppercase text-gray-500 mb-1.5">Appareil (7j)</div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 text-xs">
              <Smartphone className="w-3 h-3 text-emerald-600" />
              <span className="text-gray-700 flex-1">Mobile</span>
              <span className="font-mono font-medium">{stats.device_breakdown?.mobile || 0}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <Monitor className="w-3 h-3 text-blue-600" />
              <span className="text-gray-700 flex-1">Desktop</span>
              <span className="font-mono font-medium">{stats.device_breakdown?.desktop || 0}</span>
            </div>
            {mobilePct !== null && (
              <div className="pt-1 border-t border-gray-200 text-[10px] text-gray-500">
                {mobilePct}% mobile
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, tone, pulse, testid }) {
  const toneMap = {
    rose: "from-rose-50 to-rose-100 border-rose-200 text-rose-700",
    amber: "from-amber-50 to-amber-100 border-amber-200 text-amber-700",
    blue: "from-blue-50 to-blue-100 border-blue-200 text-blue-700",
    indigo: "from-indigo-50 to-indigo-100 border-indigo-200 text-indigo-700",
  };
  return (
    <div className={`bg-gradient-to-br ${toneMap[tone]} border rounded-lg p-2.5`} data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase font-semibold opacity-80">{label}</span>
        <Icon className={`w-3.5 h-3.5 ${pulse ? "animate-pulse" : ""}`} />
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}

function fillMissingDays(days, n) {
  const map = Object.fromEntries(days.map((d) => [d.date, d.count]));
  const out = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setUTCDate(d.getUTCDate() - i);
    const iso = d.toISOString().slice(0, 10);
    out.push({ date: iso, count: map[iso] || 0 });
  }
  return out;
}

function formatRelative(iso) {
  const t = new Date(iso);
  const diff = (Date.now() - t.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 7) return `il y a ${Math.floor(diff / 86400)}j`;
  return t.toLocaleDateString("fr-CA");
}
